import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ArrowLeft, Smartphone } from 'lucide-react';

const Login = () => {
  usePageTitle('Login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA state
  const [showTotpInput, setShowTotpInput] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  
  const { login, verifyTotp, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.requires_totp) {
      // 2FA is required
      setTempToken(result.temp_token);
      setShowTotpInput(true);
      toast.info('2FA Required', {
        description: 'Please enter your authenticator code'
      });
    } else if (result.success) {
      toast.success('Welcome back!', {
        description: `Logged in as ${result.user.first_name}`
      });
      
      if (result.user.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } else {
      toast.error('Login failed', {
        description: result.error
      });
    }
    
    setLoading(false);
  };

  const handleTotpVerify = async () => {
    if (totpCode.length !== 6 && totpCode.length !== 8) {
      toast.error('Please enter a valid code');
      return;
    }

    setLoading(true);
    
    const result = await verifyTotp(tempToken, totpCode);
    
    if (result.success) {
      toast.success('Welcome back!', {
        description: `Logged in as ${result.user.first_name}`
      });
      
      if (result.user.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } else {
      toast.error('Verification failed', {
        description: result.error
      });
    }
    
    setLoading(false);
  };

  const handleBackToLogin = () => {
    setShowTotpInput(false);
    setTempToken('');
    setTotpCode('');
  };

  // 2FA Verification Screen
  if (showTotpInput) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center gap-3 mb-4">
              <img 
                src="/emp-medic-logo.png" 
                alt="EMP Medic Logo" 
                className="h-16 w-auto"
              />
            </div>
            <p className="text-slate-600">Training Management Portal</p>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 bg-[#2C9DE1]/10 rounded-full flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-[#2C9DE1]" />
                </div>
              </div>
              <CardTitle className="text-2xl text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Two-Factor Authentication
              </CardTitle>
              <CardDescription className="text-center">
                Enter the 6-digit code from your authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                <InputOTP 
                  maxLength={6} 
                  value={totpCode} 
                  onChange={setTotpCode}
                  onComplete={handleTotpVerify}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-12 h-12 text-lg" />
                    <InputOTPSlot index={1} className="w-12 h-12 text-lg" />
                    <InputOTPSlot index={2} className="w-12 h-12 text-lg" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} className="w-12 h-12 text-lg" />
                    <InputOTPSlot index={4} className="w-12 h-12 text-lg" />
                    <InputOTPSlot index={5} className="w-12 h-12 text-lg" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                You can also use a backup code if you don't have access to your authenticator
              </p>

              <Button
                onClick={handleTotpVerify}
                className="w-full h-11 bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={loading || totpCode.length < 6}
                data-testid="totp-verify-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleBackToLogin}
                className="w-full"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex flex-col items-center gap-3 mb-4">
            <img 
              src="/emp-medic-logo.png" 
              alt="EMP Medic Logo" 
              className="h-16 w-auto"
            />
          </div>
          <p className="text-slate-600">Training Management Portal</p>
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sign In
            </CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access the portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
              
              <div className="text-center mt-4">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#2C9DE1] hover:text-[#1A7BB8] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          © {new Date().getFullYear()} EMP Medic. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
